import React, { useEffect, useRef, useState } from 'react';
import './ModalShell.scss';

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_e) {
    return false;
  }
}

/**
 * The dialog CONTAINER every modal in this app shares: the dimmed overlay, the open/close
 * animation, Escape, backdrop-click, the × button and focus handling. Content is `children`.
 *
 * It exists because AuthModal and AccountModal had each grown their own copy of all of that, and
 * the copies drifted: the × was styled by `%ui-chip` through a rule in App.scss that named
 * `.auth-modal-close` specifically, so the account dialog's × silently fell back to the browser's
 * default button chrome (2px outset, ButtonFace). One container means one × that cannot drift.
 *
 * `overlayClassName` / `className` are the per-dialog SKIN — size, layout, background. They are
 * emitted ALONGSIDE the shared classes, so each dialog's own stylesheet keeps working unchanged.
 */
export default function ModalShell({
  isOpen,
  onClose,
  onExited,
  label,
  labelledBy,
  overlayClassName,
  className,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  onExited?: () => void;
  /** Accessible name, when the dialog has no visible heading to point at. */
  label?: string;
  /** Id of the heading that names the dialog. Preferred over `label` when one exists. */
  labelledBy?: string;
  overlayClassName?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  // The control that opened the dialog, so focus goes back where it came from on close.
  const openerRef = useRef<Element | null>(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  // Start at "enter" so the first paint is hidden, then transition to "open" on the next frame
  // (otherwise there is no open animation).
  const [animState, setAnimState] = useState<'enter' | 'open' | 'exit'>(() => (isOpen ? 'enter' : 'exit'));

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setAnimState('enter');
      const raf = window.requestAnimationFrame(() => setAnimState('open'));
      return () => window.cancelAnimationFrame(raf);
    }

    if (!isOpen && shouldRender) {
      // With reduced motion there is no transition, so nothing would fire `transitionend`.
      if (prefersReducedMotion()) {
        setAnimState('exit');
        setShouldRender(false);
        onExited?.();
        return undefined;
      }
      setAnimState('exit');
      // Unmount is triggered by `onTransitionEnd` on the modal.
      return undefined;
    }

    return undefined;
  }, [isOpen, onExited, shouldRender]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open and hand it back to the opener on close — a dialog reached
  // by keyboard is otherwise a dead end, and the launcher button is what the user came from.
  useEffect(() => {
    if (!isOpen) return undefined;
    openerRef.current = document.activeElement;
    modalRef.current?.focus();
    return () => {
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={ ['modal-shell-overlay', overlayClassName].filter(Boolean).join(' ') }
      data-state={ animState }
      role="presentation"
      onMouseDown={ (e) => {
        if (e.target === e.currentTarget) onClose();
      } }
    >
      <div
        ref={ modalRef }
        className={ ['modal-shell', className].filter(Boolean).join(' ') }
        role="dialog"
        aria-modal="true"
        aria-label={ labelledBy ? undefined : label }
        aria-labelledby={ labelledBy }
        tabIndex={ -1 }
        onTransitionEnd={ (e) => {
          // Only unmount after the modal itself finishes its fade/scale-out.
          if (animState !== 'exit') return;
          if (e.target !== modalRef.current) return;
          if (e.propertyName !== 'opacity') return;
          setShouldRender(false);
          onExited?.();
        } }
      >
        <button className="modal-shell-close" type="button" onClick={ onClose } aria-label="Close">
          ×
        </button>
        { children }
      </div>
    </div>
  );
}
